<template>
<div id="app" class="home page-template-default page page-id-13 wp-custom-logo wp-embed-responsive post-image-aligned-center sticky-menu-fade sticky-enabled both-sticky-menu no-sidebar nav-below-header separate-containers header-aligned-left dropdown-hover">
  <HeadNav :selectedLanguage="selectedLanguage" page="blog" :gotodownload="gotodownload" :apk="apk" />

  <div id="page" class="site grid-container container hfeed">
    <div id="content" class="site-content">
      <div id="primary" class="content-area">
        <main id="maincontent" class="site-main">
          <article v-if="article" class="blog-article">
            <header class="blog-header">
              <h1>{{ article.title }}</h1>
              <p v-if="subtitle" class="blog-subtitle">{{ subtitle }}</p>
            </header>

            <div class="blog-content">
              <img
                v-if="article.cover"
                :src="article.cover"
                :alt="article.coverAlt || article.title"
                :class="['blog-featured-image', article.coverClass]"
              >

              <section class="content-section intro-section">
                <p v-for="(paragraph, index) in article.intro" :key="`intro-${index}`" v-html="paragraph"></p>
                <div v-for="(callout, index) in article.callouts" :key="`intro-callout-${index}`" class="content-callout" v-html="callout"></div>
              </section>

              <section class="content-section facts-section">
                <h2>{{ article.factsHeading || `${article.title} at a Glance` }}</h2>
                <p v-for="(paragraph, index) in article.factsIntro" :key="`facts-intro-${index}`" v-html="paragraph"></p>
                <div v-if="article.factsDisplay === 'table'" class="table-wrapper">
                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th>Information</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="fact in article.facts" :key="fact.label">
                        <td>{{ fact.label }}</td>
                        <td>{{ fact.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="facts-grid">
                  <div v-for="fact in article.facts" :key="fact.label" class="fact-card">
                    <div class="fact-label">{{ fact.label }}</div>
                    <div class="fact-value">{{ fact.value }}</div>
                  </div>
                </div>
                <p v-for="(paragraph, index) in article.factsOutro" :key="`facts-outro-${index}`" v-html="paragraph"></p>
                <a v-if="article.downloadLink" class="download-cta" :href="article.downloadLink.href">
                  <img src="@/assets/android.webp" alt="" class="download-cta-icon">
                  <span>{{ article.downloadLink.text }}</span>
                </a>
              </section>

              <section
                v-for="table in article.tables"
                :id="table.id"
                :key="table.id"
                class="content-section"
              >
                <h2>{{ table.heading }}</h2>
                <p v-for="(paragraph, index) in table.intro" :key="`${table.id}-intro-${index}`" v-html="paragraph"></p>
                <div class="table-wrapper">
                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th v-for="heading in table.headers" :key="heading" v-html="heading"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in table.rows" :key="row[0]">
                        <td v-for="cell in row" :key="cell" v-html="cell"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-for="(paragraph, index) in table.outro" :key="`${table.id}-outro-${index}`" v-html="paragraph"></p>
                <a v-if="table.downloadLink" class="download-cta" :href="table.downloadLink.href">
                  <img src="@/assets/android.webp" alt="" class="download-cta-icon">
                  <span>{{ table.downloadLink.text }}</span>
                </a>
              </section>

              <section
                v-for="section in article.sections"
                :id="section.id"
                :key="section.id"
                class="content-section"
              >
                <h2>{{ section.heading }}</h2>
                <p v-for="(paragraph, index) in section.paragraphs" :key="`${section.id}-${index}`" v-html="paragraph"></p>
                <div v-if="section.table" class="table-wrapper">
                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th v-for="heading in section.table.headers" :key="heading" v-html="heading"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in section.table.rows" :key="row[0]">
                        <td v-for="cell in row" :key="cell" v-html="cell"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-for="(paragraph, index) in section.afterTable" :key="`${section.id}-after-table-${index}`" v-html="paragraph"></p>
                <ul v-if="section.bullets && section.bullets.length" :class="section.bulletClass">
                  <li v-for="bullet in section.bullets" :key="bullet" v-html="bullet"></li>
                </ul>
                <p v-for="(paragraph, index) in section.afterBullets" :key="`${section.id}-after-bullets-${index}`" v-html="paragraph"></p>
                <ol
                  v-if="section.steps && section.steps.length"
                  :class="{ 'explicit-steps': hasExplicitSteps(section.steps) }"
                >
                  <li v-for="step in section.steps" :key="step" v-html="step"></li>
                </ol>
                <p v-for="(paragraph, index) in section.afterSteps" :key="`${section.id}-after-steps-${index}`" v-html="paragraph"></p>
                <div v-for="subsection in section.subsections" :key="subsection.heading" class="subsection">
                  <h3>{{ subsection.heading }}</h3>
                  <p v-for="(paragraph, index) in subsection.paragraphs" :key="`${section.id}-${subsection.heading}-${index}`" v-html="paragraph"></p>
                </div>
                <div v-for="(callout, index) in section.callouts" :key="`${section.id}-callout-${index}`" class="content-callout" v-html="callout"></div>
                <a v-if="section.downloadLink" class="download-cta" :href="section.downloadLink.href">
                  <img src="@/assets/android.webp" alt="" class="download-cta-icon">
                  <span>{{ section.downloadLink.text }}</span>
                </a>
                <ul v-if="section.secondaryBullets && section.secondaryBullets.length">
                  <li v-for="bullet in section.secondaryBullets" :key="bullet" v-html="bullet"></li>
                </ul>
                <p v-for="(paragraph, index) in section.finalParagraphs" :key="`${section.id}-final-${index}`" v-html="paragraph"></p>
              </section>

              <section id="faq" class="content-section">
                <h2>{{ article.faqHeading || `${article.title} FAQ` }}</h2>
                <div v-for="item in article.faqs" :key="item.question" class="faq-item">
                  <h3>{{ item.question }}</h3>
                  <p v-html="item.answer"></p>
                </div>
              </section>

              <section v-if="article.finalTake" id="final-take" class="content-section">
                <h2>{{ article.finalTake.heading }}</h2>
                <p v-for="(paragraph, index) in article.finalTake.paragraphs" :key="`final-take-${index}`" v-html="paragraph"></p>
              </section>

            </div>
          </article>
        </main>
      </div>
    </div>
  </div>

  <div v-if="$global.isMobileDevice()" id="fixed-bottom-btn" class="fix-bottom-btn" @click="gotoDownloadPage()">
    Go to GB WhatsApp Download
  </div>
</div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import '@/css/blog/blogspc.scss';
import HeadNav from '@/components/HeadNav.vue';
import modApps from '@/documents/mod-apps';

export default {
  name: 'ModApp',
  components: {
    HeadNav,
  },
  data() {
    return {
      selectedLanguage: 'en',
      apk: null,
      from: 'gb',
      filename: 'gb',
    };
  },
  computed: {
    article() {
      return modApps[this.$route.meta.modKey] || null;
    },
    subtitle() {
      if (!this.article) {
        return '';
      }
      if (this.article.subtitle === false) {
        return '';
      }

      return this.article.subtitle || `${this.article.title} features, update rhythm, and how it compares with GB WhatsApp.`;
    },
  },
  mounted() {
    this.from = this.$global.GetQueryString('from') || this.from;
    this.filename = this.$global.GetQueryString('filename') || this.filename;
    if (this.$global.GetQueryString('from')) {
      this.getAPK();
    } else {
      this.getOfficalApk();
    }
  },
  methods: {
    getAPK() {
      this.$server
        .getAPPInfo({
          promotionChannel: this.from,
          promotionName: this.filename,
        })
        .then((res) => {
          if (res.code == 200) {
            this.apk = res.data.apk;
          } else {
            this.apk = process.env.VUE_APP_GBLINK;
          }
        });
    },
    getOfficalApk(type = 1, domain = null, appname = null) {
      this.$server
        .getOfficalApk({
          domain: domain || 'gbwhataps.com',
          appName: appname || 'GB_gbwhataps.com',
        })
        .then((res) => {
          if (res.code == 200) {
            if (type == 1) {
              this.apk = res.data.apk;
            }
          } else {
            this.apk = process.env.VUE_APP_GBLINK;
          }
        });
    },
    hasExplicitSteps(steps) {
      return steps.some((step) => /Step\s*\d+|步骤\s*\d+/i.test(step));
    },
    gotodownload() {
      if (this.apk) {
        this.$global.download(this.apk);
      }
    },
    gotoDownloadPage() {
      this.$router.push({
        name: 'en-download'
      });
    },
  },
};
</script>

<style scoped>
.blog-article {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.blog-header {
  text-align: center;
  margin-bottom: 30px;
}

.blog-header h1 {
  font-size: 2.5em;
  color: #25d366;
  margin-bottom: 12px;
  line-height: 1.2;
}

.blog-subtitle {
  color: #4f5d68;
  font-size: 1.05em;
  margin: 0 auto;
  max-width: 720px;
}

.blog-content {
  line-height: 1.8;
  font-size: 1.05em;
  counter-reset: callout-counter;
}

.blog-featured-image {
  display: block;
  width: 100%;
  max-width: 260px;
  height: auto;
  margin: 0 auto 28px;
  border-radius: 8px;
}

.content-section {
  margin-bottom: 32px;
}

.content-section h2 {
  color: #25d366;
  font-size: 1.8em;
  margin-bottom: 16px;
  border-left: 4px solid #25d366;
  padding-left: 15px;
}

.content-section h3 {
  color: #128c7e;
  margin-bottom: 10px;
}

.content-section p {
  margin-bottom: 15px;
  color: #333;
}

.content-section ul {
  margin: 15px 0;
  padding-left: 0;
  list-style: none;
}

.content-section ol {
  margin: 15px 0;
  padding-left: 0;
  list-style: none;
  counter-reset: content-step;
}

.content-section li {
  position: relative;
  margin-bottom: 8px;
  padding-left: 24px;
  color: #333;
  list-style: none;
}

.content-section ul:not(.check-list) > li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.72em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #333;
}

.content-section ol > li {
  counter-increment: content-step;
}

.content-section ol > li::before {
  content: counter(content-step) ".";
  position: absolute;
  left: 0;
  top: 0;
  color: #333;
  font-weight: 700;
}

.content-section ol.explicit-steps > li {
  padding-left: 0;
}

.content-section ol.explicit-steps > li::before {
  content: none;
}

.content-section .check-list {
  padding-left: 0;
  list-style: none;
}

.content-section .check-list li {
  padding-left: 24px;
}

.content-section .check-list li::marker {
  content: "";
}

.content-section .check-list li::before {
  content: "\221A";
  position: absolute;
  left: 0;
  top: 0;
  color: #1f2933;
  font-weight: 700;
}

.subsection {
  margin-top: 18px;
}

.content-callout {
  position: relative;
  margin: 25px 0;
  padding: 22px 30px 22px 56px;
  border: 2px solid rgba(37, 211, 102, 0.1);
  border-left: 6px solid #25d366;
  border-radius: 0 15px 15px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0fdf4 100%);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  color: #2f3b45;
  text-align: left;
  counter-increment: callout-counter;
}

.content-callout::before {
  content: counter(callout-counter);
  position: absolute;
  top: 16px;
  left: 16px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #25d366;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
}

.content-callout::after {
  content: "\f10e";
  position: absolute;
  top: 8px;
  right: 18px;
  color: #25d366;
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  font-size: 48px;
  line-height: 1;
  opacity: 0.18;
}

.download-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 44px;
  margin: 8px 0 18px;
  padding: 0 18px;
  border-radius: 6px;
  background: #25d366;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
}

.download-cta-icon {
  display: block;
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  filter: brightness(0) invert(1);
}

.download-cta:hover {
  background: #128c7e;
  color: #fff;
}

.facts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.fact-card {
  padding: 18px;
  border: 1px solid rgba(37, 211, 102, 0.18);
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f3fff8 100%);
}

.fact-label {
  color: #128c7e;
  font-weight: 700;
  margin-bottom: 8px;
}

.fact-value {
  color: #2f3b45;
  font-size: 0.96em;
}

.table-wrapper {
  width: 100%;
  margin: 20px 0;
  border: 1px solid #b8d8cd;
  border-radius: 8px;
  overflow-x: auto;
}

.comparison-table {
  width: 100%;
  min-width: 640px;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
}

.comparison-table th,
.comparison-table td {
  padding: 9px 12px;
  border-right: 1px solid #dce8e4;
  border-bottom: 1px solid #dce8e4;
  color: #333;
  font-size: 0.92em;
  line-height: 1.35;
  text-align: left;
  vertical-align: top;
}

.comparison-table th:last-child,
.comparison-table td:last-child {
  border-right: 0;
}

.comparison-table tbody tr:last-child td {
  border-bottom: 0;
}

.comparison-table th {
  background: #e9f7ef;
  color: #128c7e;
  font-weight: 700;
  font-size: 0.94em;
  padding-top: 8px;
  padding-bottom: 8px;
}

.comparison-table a {
  color: #128c7e;
  text-decoration: none;
}

.comparison-table a:hover {
  text-decoration: underline;
}

.faq-item + .faq-item {
  margin-top: 20px;
}

@media (max-width: 768px) {
  .blog-article {
    padding: 15px;
  }

  .blog-header h1 {
    font-size: 2em;
  }

  .content-section h2 {
    font-size: 1.5em;
  }

  .facts-grid {
    grid-template-columns: 1fr;
  }

  .content-callout {
    padding: 15px 20px 15px 52px;
    font-size: 14px;
  }

  .content-callout::before {
    font-size: 13px;
    top: 15px;
    left: 15px;
  }
}
</style>
